import {useCallback, useEffect, useState} from 'react';

import {fetchAtRiskSurvey, submitAtRiskSurveyAnswer} from '../api/at-risk-survey-api';

import type {SurveyStructure} from '../api/at-risk-survey-api';
import type {SurveyAnswerOption} from '../types';

export interface UseAtRiskSurveyResult {
    isLoading: boolean;
    error: Error | null;
    answerOptions: SurveyAnswerOption[];
    submitAnswer: (optionValue: string, freeformText?: string) => Promise<void>;
}

export function useAtRiskSurvey(surveyCode: string): UseAtRiskSurveyResult {
    const [survey, setSurvey] = useState<SurveyStructure | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        fetchAtRiskSurvey(surveyCode)
            .then((data) => {
                if (!cancelled) setSurvey(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [surveyCode]);

    const submitAnswer = useCallback(
        async (optionCode: string, freeformText?: string) => {
            if (!survey || !survey.choiceQuestionId) {
                throw new Error('Survey not loaded');
            }

            await submitAtRiskSurveyAnswer({
                surveyId: survey.surveyId,
                choiceQuestionId: survey.choiceQuestionId,
                optionId: survey.optionIdByCode[optionCode] ?? null,
                freeformQuestionId: survey.freeformQuestionId,
                freeformText,
            });
        },
        [survey],
    );

    return {
        isLoading,
        error,
        answerOptions: survey?.answerOptions ?? [],
        submitAnswer,
    };
}
